import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import { type ReactNode, useState } from "react";
import { LockedBadge } from "../unboxing/LockedBadge";

interface ProFeaturesAccordionProps {
  /** MUI icon element rendered beside the title. */
  icon: ReactNode;
  title: string;
  /** Plan-gated: renders the Upgrade badge + dims/disables the body until unlocked. */
  locked: boolean;
  lockTier?: "upgrade" | "enterprise";
  /** Label on the bottom unlock bar (e.g. "Unlock Custom Colors"). */
  unlockLabel: string;
  /** Opens this feature's upgrade modal. Fired by both the header Upgrade badge and the
   *  bottom unlock bar. */
  onUpgrade: () => void;
  defaultOpen?: boolean;
  children: ReactNode;
}

/**
 * Collapsible Pro-feature section for the Landing → Branding tab (Custom Colors,
 * Confetti). Ported from the prototype's `.pro-features-accordion`: header with an
 * Upgrade badge, and — when locked — a dimmed, non-interactive body plus an "Unlock"
 * bar. Both the badge and the bar open a feature-specific upgrade modal via `onUpgrade`.
 * Unlocked (Pro/Enterprise) → fully interactive.
 */
export default function ProFeaturesAccordion({
  icon,
  title,
  locked,
  lockTier = "upgrade",
  unlockLabel,
  onUpgrade,
  defaultOpen = false,
  children,
}: ProFeaturesAccordionProps) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div
      className="mt-4 overflow-hidden rounded-lg"
      style={{ border: "1px solid var(--color-card-border)" }}
    >
      {/* Header row is a plain flex container — the title + chevron toggle, the Upgrade
          badge is its own button (so it opens the modal without also toggling / nesting). */}
      <div className="flex w-full items-center justify-between bg-white px-3.5 py-3">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex flex-1 items-center gap-2 border-none bg-transparent p-0 text-left font-lato text-[13px] font-bold text-rodeo-black cursor-pointer"
        >
          <span className="flex text-dark-dust">{icon}</span>
          {title}
        </button>
        <span className="flex items-center gap-2">
          {locked && <LockedBadge tier={lockTier} onClick={onUpgrade} />}
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? `Collapse ${title}` : `Expand ${title}`}
            aria-expanded={open}
            className="flex items-center border-none bg-transparent p-0 cursor-pointer"
          >
            <ExpandMoreIcon
              sx={{
                fontSize: 20,
                color: "var(--color-graphite)",
                transform: open ? "rotate(180deg)" : "none",
                transition: "transform 0.2s",
              }}
            />
          </button>
        </span>
      </div>

      {open && (
        <div style={{ borderTop: "1px solid var(--color-card-border)" }}>
          <div
            className={locked ? "pointer-events-none opacity-50" : ""}
            style={{ padding: "16px 14px" }}
            aria-disabled={locked}
          >
            {children}
          </div>
          {locked && (
            <button
              type="button"
              onClick={onUpgrade}
              className="flex w-full items-center justify-center gap-1.5 border-t border-card-border bg-wax py-2.5 font-lato text-[12px] font-bold uppercase tracking-[0.5px] text-dust-ink cursor-pointer hover:bg-light-dust transition-colors"
            >
              <LockOpenIcon sx={{ fontSize: 15 }} />
              {unlockLabel}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
